import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import PolicySections, { PolicyTextarea } from "@/components/PolicyContent";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { SECTION_LABELS } from "@/lib/policyLabels";

const ROLES = [["travel_trainer", "Travel Trainer"], ["admin_manager", "Co-ordinator"], ["super_admin", "Manager"]];
const OWNERS = ["Responsible Individual / Director", "Service Manager", "Safeguarding Lead", "Data Protection Lead", "Training Coordinator", "Operations Manager"];

export default function PolicyEditorPage() {
  const { policyId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("policies:manage");
  const isNew = !policyId;

  const [meta, setMeta] = useState({
    title: "", doc_type: "policy", category: "", owner_role: "", applicable_roles: [],
    mandatory: true, classification: "Internal", requires_formal_review: false, next_review_date: "",
  });
  const [sections, setSections] = useState({});
  const [versionId, setVersionId] = useState(params.get("version"));
  const [quickSteps, setQuickSteps] = useState("");
  const [check, setCheck] = useState([]);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (isNew) return;
    try {
      const { data } = await api.get(`/policies/${policyId}`);
      setMeta({
        title: data.title, doc_type: data.doc_type, category: data.category,
        owner_role: data.owner_role || "", applicable_roles: data.applicable_roles || [],
        mandatory: data.mandatory, classification: data.classification || "Internal",
        requires_formal_review: data.requires_formal_review, next_review_date: data.next_review_date || "",
      });
      setQuickSteps((data.quick_steps || []).join("\n"));
      setCheck(data.knowledge_check_full || []);
      const vid = versionId || data.version_detail?.id;
      if (data.version_detail) {
        setSections(data.version_detail.sections || {});
        setVersionId(data.version_detail.id);
      }
      if (vid && !data.version_detail) {
        const { data: det } = await api.get(`/policies/${policyId}`);
        setSections(det.version_detail?.sections || {});
      }
      // knowledge check answers are stripped in the detail view for non-managers; managers get them via this page only
      if (data.has_knowledge_check) {
        setCheck((data.knowledge_check || []).map((q) => ({ question: q.question, options: q.options, answer_index: q.answer_index ?? 0 })));
      }
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/policies");
    }
  }, [policyId, isNew, navigate, versionId]);

  useEffect(() => { load(); }, [load]);

  if (!canManage) {
    return <p className="text-sm text-slate-600" data-testid="editor-denied">Only authorised managers can edit policies.</p>;
  }

  const toggleRole = (r) =>
    setMeta((m) => ({ ...m, applicable_roles: m.applicable_roles.includes(r) ? m.applicable_roles.filter((x) => x !== r) : [...m.applicable_roles, r] }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...meta,
        owner_role: meta.owner_role || null,
        next_review_date: meta.next_review_date || null,
        quick_steps: quickSteps.split("\n").map((s) => s.trim()).filter(Boolean),
        knowledge_check: check.filter((q) => q.question.trim()),
      };
      if (isNew) {
        const { data } = await api.post("/policies", { ...payload, sections });
        toast.success(`Policy ${data.reference} created as draft v1.0`);
        navigate(`/policies/${data.policy_id}/edit?version=${data.version_id}`);
      } else {
        await api.patch(`/policies/${policyId}`, payload);
        if (versionId) await api.patch(`/policies/versions/${versionId}`, { sections });
        toast.success("Draft saved — nothing is published until you submit, approve and publish");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 max-w-4xl" data-testid="policy-editor">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-primary" data-testid="editor-back">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <h1 className="font-heading text-2xl font-bold text-slate-900">{isNew ? "New policy" : `Editing draft — ${meta.title}`}</h1>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" className="h-10" data-testid="editor-preview" onClick={() => setPreview((v) => !v)}>
            {preview ? "Back to editor" : "Preview as staff see it"}
          </Button>
          <Button className="h-10 bg-primary text-white" data-testid="editor-save" disabled={saving || meta.title.length < 3 || !meta.category} onClick={save}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save draft</>}
          </Button>
        </div>
      </div>

      {preview ? (
        <Card data-testid="editor-preview-pane">
          <CardHeader><CardTitle>{meta.title || "Untitled"}</CardTitle></CardHeader>
          <CardContent><PolicySections sections={sections} /></CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2"><Label>Title *</Label>
                <Input className="h-11" data-testid="editor-title" value={meta.title} onChange={(e) => setMeta((m) => ({ ...m, title: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Type</Label>
                <Select value={meta.doc_type} onValueChange={(v) => setMeta((m) => ({ ...m, doc_type: v }))}>
                  <SelectTrigger className="h-11" data-testid="editor-doctype"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="policy">Policy</SelectItem>
                    <SelectItem value="procedure">Procedure</SelectItem>
                    <SelectItem value="privacy_notice">Privacy notice</SelectItem>
                    <SelectItem value="guidance">Guidance</SelectItem>
                  </SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Category *</Label>
                <Input className="h-11" data-testid="editor-category" value={meta.category} onChange={(e) => setMeta((m) => ({ ...m, category: e.target.value }))} placeholder="e.g. Safeguarding" /></div>
              <div className="space-y-1.5"><Label>Policy owner</Label>
                <Select value={meta.owner_role || "none"} onValueChange={(v) => setMeta((m) => ({ ...m, owner_role: v === "none" ? "" : v }))}>
                  <SelectTrigger className="h-11" data-testid="editor-owner"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">Unassigned</SelectItem>
                    {OWNERS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Next review date</Label>
                <Input type="date" className="h-11" data-testid="editor-review-date" value={meta.next_review_date} onChange={(e) => setMeta((m) => ({ ...m, next_review_date: e.target.value }))} /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>Applies to (no selection = all staff)</Label>
                <div className="flex flex-wrap gap-2">
                  {ROLES.map(([val, label]) => (
                    <button key={val} type="button" data-testid={`editor-role-${val}`} onClick={() => toggleRole(val)}
                      className={`rounded-md border px-3 py-2 text-xs font-medium min-h-[40px] ${meta.applicable_roles.includes(val) ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-border"}`}>
                      {label}
                    </button>
                  ))}
                </div></div>
              <div className="flex flex-wrap gap-4 sm:col-span-2">
                <label className="flex items-center gap-2 text-sm" data-testid="editor-mandatory-wrap">
                  <input type="checkbox" data-testid="editor-mandatory" checked={meta.mandatory} onChange={(e) => setMeta((m) => ({ ...m, mandatory: e.target.checked }))} className="h-4 w-4" /> Mandatory for applicable staff
                </label>
                <label className="flex items-center gap-2 text-sm" data-testid="editor-formal-wrap">
                  <input type="checkbox" data-testid="editor-formal" checked={meta.requires_formal_review} onChange={(e) => setMeta((m) => ({ ...m, requires_formal_review: e.target.checked }))} className="h-4 w-4" /> Requires formal organisational review before publication
                </label>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Content sections</CardTitle>
              <p className="text-xs text-slate-500">Use the toolbar or type formatting directly: **bold**, "## " heading, "- " bullets, "1." numbered steps,
                [link text](https://…) or [internal link](/policies), "!warn " warning box, "!action " quick-action box, and pipe tables (| col | col |).
                Content is rendered safely — no code or scripts are stored or shown.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(SECTION_LABELS).map(([key, label]) => (
                <div key={key} className="space-y-1.5">
                  <Label>{label}</Label>
                  <PolicyTextarea rows={key === "procedure" ? 6 : 3} testid={`editor-section-${key}`} value={sections[key] || ""}
                    onChange={(e) => setSections((s) => ({ ...s, [key]: e.target.value }))} />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Quick procedure card (optional)</CardTitle>
              <p className="text-xs text-slate-500">One step per line. Shows as a mobile quick-action card and in Quick Procedures.</p></CardHeader>
            <CardContent>
              <Textarea rows={4} data-testid="editor-quick-steps" value={quickSteps} onChange={(e) => setQuickSteps(e.target.value)}
                placeholder={"Call 999 if anyone is in danger\nPhone the office\nRecord the incident"} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Knowledge check (optional)</CardTitle>
                  <p className="text-xs text-slate-500">Short questions staff complete after reading. A completion record only — never a competence measure.</p>
                </div>
                <Button variant="outline" className="h-9 text-xs" data-testid="editor-add-question" disabled={check.length >= 5}
                  onClick={() => setCheck((c) => [...c, { question: "", options: ["", "", ""], answer_index: 0 }])}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add question
                </Button>
              </div>
            </CardHeader>
            {check.length > 0 && (
              <CardContent className="space-y-4">
                {check.map((q, qi) => (
                  <div key={qi} className="rounded-md border border-border p-3 space-y-2" data-testid={`editor-question-${qi}`}>
                    <div className="flex items-center gap-2">
                      <Input className="h-10" placeholder={`Question ${qi + 1}`} value={q.question} data-testid={`editor-question-text-${qi}`}
                        onChange={(e) => setCheck((c) => c.map((x, i) => i === qi ? { ...x, question: e.target.value } : x))} />
                      <button type="button" data-testid={`editor-question-delete-${qi}`} onClick={() => setCheck((c) => c.filter((_, i) => i !== qi))}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-md"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    {q.options.map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <input type="radio" name={`correct-${qi}`} checked={q.answer_index === oi} data-testid={`editor-question-correct-${qi}-${oi}`}
                          onChange={() => setCheck((c) => c.map((x, i) => i === qi ? { ...x, answer_index: oi } : x))} className="h-4 w-4" />
                        <Input className="h-9 text-sm" placeholder={`Option ${oi + 1}`} value={opt} data-testid={`editor-question-opt-${qi}-${oi}`}
                          onChange={(e) => setCheck((c) => c.map((x, i) => i === qi ? { ...x, options: x.options.map((o, j) => j === oi ? e.target.value : o) } : x))} />
                      </div>
                    ))}
                    <p className="text-[10px] text-slate-400">Selected radio = correct answer.</p>
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
